import React, {
  createContext,
  useContext,
  useRef,
  useState,
  useCallback,
} from 'react';
import {
  TTSEmotion,
  VideoEditorTTSVoice,
} from '@/shared/apis/tts/get-tts-voices';
import type { GeminiPreviewLanguage } from '@/shared/components/dialog/TtsDialog/useGeminiPreviewUrl';

export type TTSVoice = {
  voice: VideoEditorTTSVoice;
  tempo: number;
  pitch: number | null;
  emotion: TTSEmotion;
  style?: string;
  /**
   * voice-preset 진입점에서 선택된 경우 — caller (panel) 가 voice 이름 대신 사용자가 붙인 preset 이름을 표시.
   * 미설정 시 voice.name / voice.nameKorean 사용 (기존 동작).
   */
  presetName?: string;
};

export interface PlayPreviewAudioOptions {
  onError?: (error?: unknown) => void;
}

export interface TtsDialogContextValue {
  // Voice 관련
  selectedVoice: VideoEditorTTSVoice | null;
  setSelectedVoice: (voice: VideoEditorTTSVoice) => void;

  // Emotion 관련
  selectedEmotion: TTSEmotion | null;
  setSelectedEmotion: (emotion: TTSEmotion | null) => void;

  // Tempo 관련
  tempo: number;
  setTempo: (tempo: number) => void;

  // Pitch 관련
  pitch: number;
  setPitch: (pitch: number) => void;

  // Style directive (Gemini 전용 자연어 지시)
  style: string;
  setStyle: (style: string) => void;

  // 전역 오디오 재생 관리
  currentPlayingId: string | null;
  playPreviewAudio: (
    playingId: string,
    audio: HTMLAudioElement,
    options?: PlayPreviewAudioOptions,
  ) => void;
  stopCurrentAudio: () => void;

  // preview 재생 언어 override. 미지정 시 카드가 locale 기준으로 결정한다.
  // MTS 처럼 UI locale ≠ 콘텐츠 언어인 경우 caller 가 명시 주입한다.
  previewLanguage?: GeminiPreviewLanguage;
}

const TtsDialogContext = createContext<TtsDialogContextValue | null>(null);

export interface TtsDialogProviderProps {
  children: React.ReactNode;
  initialVoice?: TTSVoice | null;
  previewLanguage?: GeminiPreviewLanguage;
}

export function TtsDialogProvider({
  children,
  initialVoice = null,
  previewLanguage,
}: TtsDialogProviderProps) {
  const [selectedVoice, setSelectedVoice] =
    useState<VideoEditorTTSVoice | null>(initialVoice?.voice ?? null);
  const [selectedEmotion, setSelectedEmotion] = useState<TTSEmotion | null>(
    initialVoice?.emotion ?? null,
  );
  const [tempo, setTempo] = useState(initialVoice?.tempo ?? 1.0); // 기본값: 1.0 (정상 속도)
  const [pitch, setPitch] = useState<number>(initialVoice?.pitch ?? 0); // 기본값: 0
  const [style, setStyle] = useState<string>(initialVoice?.style ?? ''); // Gemini 전용

  // 전역 오디오 재생 관리 (detail/list 구분)
  const [currentPlayingId, setCurrentPlayingId] = useState<string | null>(null);
  const currentAudioRef = useRef<HTMLAudioElement | null>(null);

  // 현재 재생 중인 오디오 중지
  const stopCurrentAudio = useCallback(() => {
    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
      currentAudioRef.current.currentTime = 0;
      currentAudioRef.current = null;
    }
    setCurrentPlayingId(null);
  }, []);

  // 새로운 오디오 재생 (기존 재생 중단)
  const playPreviewAudio = useCallback(
    (
      playingId: string,
      audio: HTMLAudioElement,
      options?: PlayPreviewAudioOptions,
    ) => {
      // 기존 재생 중단
      stopCurrentAudio();

      // 새로운 오디오 설정
      currentAudioRef.current = audio;
      setCurrentPlayingId(playingId);
      let playbackFailureHandled = false;

      const cleanup = () => {
        audio.removeEventListener('ended', handleEnded);
        audio.removeEventListener('pause', handleEnded);
        audio.removeEventListener('error', handleAudioElementError);
      };

      // 재생 종료 시 상태 초기화
      function handleEnded() {
        cleanup();
        if (currentAudioRef.current === audio) {
          currentAudioRef.current = null;
          setCurrentPlayingId(null);
        }
      }

      function handlePlaybackError(error?: unknown) {
        cleanup();
        if (playbackFailureHandled || currentAudioRef.current !== audio) return;
        playbackFailureHandled = true;
        currentAudioRef.current = null;
        setCurrentPlayingId(null);
        options?.onError?.(error ?? audio.error ?? undefined);
      }

      function handleAudioElementError() {
        handlePlaybackError(audio.error ?? undefined);
      }

      audio.addEventListener('ended', handleEnded);
      audio.addEventListener('pause', handleEnded);
      audio.addEventListener('error', handleAudioElementError);

      // 재생
      audio.play().catch(handlePlaybackError);
    },
    [stopCurrentAudio],
  );

  const value: TtsDialogContextValue = {
    selectedVoice,
    setSelectedVoice,
    selectedEmotion,
    setSelectedEmotion,
    tempo,
    setTempo,
    pitch,
    setPitch,
    style,
    setStyle,
    currentPlayingId,
    playPreviewAudio,
    stopCurrentAudio,
    previewLanguage,
  };

  return (
    <TtsDialogContext.Provider value={value}>
      {children}
    </TtsDialogContext.Provider>
  );
}

export function useTtsDialog() {
  const context = useContext(TtsDialogContext);
  if (!context) {
    throw new Error('useTtsDialog must be used within TtsDialogProvider');
  }
  return context;
}
